import React from 'react';

import type { AugmentedFile } from 'js/src/libs/native-file-handler';
import NativeFileHandler from 'js/src/libs/native-file-handler';

// workaround to support 'directory' and 'webkitdirectory' attribute on input element
declare module 'react' {
    interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
        // extends React's HTMLAttributes
        directory?: string;
        webkitdirectory?: string;
    }
}

type Props = {
    onDragEnter?: VoidFunction; // a callback event that indicates a folder/file has been dragged on top of the input.
    onDragLeave?: VoidFunction; // a callback event that indicates dragging a folder/file on top of the input has stopped.
    onChange: (files: AugmentedFile[]) => void; //  a callback that accepts an array of files that have been selected by the filepicker when they have changed
    disabled?: boolean;
    accept?: string;
    disableMultipleFileUpload?: boolean;
    'data-testid'?: string;
};

const FileInput = React.memo(function FileInput({
    onDragEnter,
    onDragLeave,
    onChange,
    disabled,
    accept,
    disableMultipleFileUpload,
    'data-testid': dataTestId,
}: Props) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    /**
     * This will force component to re-render the <input> element.
     * This will ensure that the previously selected webkitEntries
     * are not being cached.
     */
    const [count, setCount] = React.useState(1);

    const sendChangeEvent = React.useCallback(
        (fileList: AugmentedFile[]) => {
            onChange(fileList);

            if (!inputRef.current) {
                throw new Error('inputRef is not defined');
            }

            // we reset the input to have a blank value, so that if the user
            // reselects the same file, the onChange event will be fired again
            inputRef.current.value = '';
            // Force instantiating a new <input> element, this ensures the
            // input element doesn't cache the previously selected webkitEntries.
            setCount((prev) => prev + 1);
        },
        [onChange],
    );

    const onDrop = React.useCallback(
        (e: React.DragEvent<HTMLInputElement>) => {
            NativeFileHandler.getFilesFromDropEvent(e, sendChangeEvent);
        },
        [sendChangeEvent],
    );

    const onChangeInternal = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            NativeFileHandler.getFilesFromChangeEvent(e, sendChangeEvent);
        },
        [sendChangeEvent],
    );

    const canUploadFolders = window.SERVER?.USER?.can_upload_media_folders;
    const acceptFileTypes = accept || '*';

    return (
        <input
            ref={inputRef}
            type="file"
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            disabled={disabled === true}
            accept={acceptFileTypes}
            onDrop={onDrop}
            onChange={onChangeInternal}
            className="file-input file-styled"
            name="file-upload-field"
            multiple={!disableMultipleFileUpload}
            data-testid={dataTestId}
            {...(canUploadFolders ? { webkitdirectory: 'true' } : {})}
            key={count} // Force re-instantiating a new <input>
        />
    );
});

export default FileInput;
